import {
  Text,
  Flex,
  Box,
  Spacer,
  Alert,
  AlertIcon,
  Button,
  useToast,
} from '@chakra-ui/react';
import React from 'react';

import { finleyAssertUnreachable } from '@finley-technologies/common';
import { FinleyAi } from '@finley-technologies/finley-types';

import { useUserAuthOrThrow } from '../../../contexts/UserAuthProvider';
import { useGetAthenaSqlGenerationSignedUrlsLazyQueryGQLType } from '../requests/ai.gql.generated';
import {
  FinleyAiPromptResponseSqlDisplay,
  FinleyAiUserBadge,
  FinleyThinkingSpinner,
  OnClickSqlAction,
  PromptChatBubble,
  PromptSuggestionButton,
} from './FinleyAiThreadComponents';
import { parsePromptResponse } from './FinleyAiUtils';
import { colors } from 'packages/common-frontend/colors';
import { errorToastConfig } from 'packages/common-frontend/constants';

// @dani add back in after streaming PR is complete

// const CsvResultPreviewTable = ({
//   finleyAiPromptResponseId,
// }: {
//   finleyAiPromptResponseId: string;
// }) => {
//   const {
//     data: previewData,
//     loading: isPreviewLoading,
//   } = useGetFinleyAiAthenaSqlGenerationPromptResultPreviewQuery({
//     variables: { finleyAiPromptResponseId, numberOfPreviewRows: 50 },
//   });

//   if (!previewData || isPreviewLoading) {
//     return <Skeleton h="100px" />;
//   }

//   const {
//     getFinleyAiAthenaSqlGenerationPromptResultPreview: previewResult,
//   } = previewData;

//   const previewCsvData: Util.CsvData = previewResult
//     ? JSON.parse(previewResult)
//     : null;

//   if (!previewCsvData || !previewCsvData.length) {
//     return null;
//   }

//   const headers = Object.keys(previewCsvData[0]);

//   return (
//     <Table>
//       <Thead>
//         <Tr>
//           {headers.map((header) => (
//             <Th key={`${header}`}>{header}</Th>
//           ))}
//         </Tr>
//       </Thead>

//       <Tbody>
//         {previewCsvData.map((csvDataRowJson, idx) => {
//           const rowKey = idx;

//           return (
//             <Tr key={`${rowKey}`}>
//               {_.map(csvDataRowJson, (value: any, fieldKey: string) => {
//                 const cellKey = `row-${idx}-field-${fieldKey}`;
//                 return <Td key={cellKey}>{value.toString()}</Td>;
//               })}
//             </Tr>
//           );
//         })}
//       </Tbody>
//     </Table>
//   );
// };

const SqlGeneratePromptResultOptions = ({
  finleyAiPromptResponseId,
  promptResult,
  handleAthenaSqlExplain,
}: {
  finleyAiPromptResponseId: string;
  promptResult: FinleyAi.SqlGenerationPromptResult;
  handleAthenaSqlExplain: (sql: string) => void;
}) => {
  const toast = useToast();

  const [
    getAthenaSqlGenerationSignedUrls,
    { loading: isSignedUrlLoading },
  ] = useGetAthenaSqlGenerationSignedUrlsLazyQueryGQLType({
    onError: () => {
      toast({
        ...errorToastConfig,
        title: `An error occurred while attempting to download csv result.`,
      });
    },
    onCompleted: ({ getSignedDownloadUrls }) => {
      if (getSignedDownloadUrls.length === 1) {
        const { url, filename } = getSignedDownloadUrls[0];

        // download
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
      } else {
        toast({
          ...errorToastConfig,
          title: `An error occurred while attempting to download csv result.`,
        });
      }
    },
    variables: {
      serializedParams: JSON.stringify({
        finleyAiPromptResponseId,
      }),
    },
    fetchPolicy: 'network-only',
  });

  const { sql, queryResultCsvS3Bucket, queryResultCsvS3Path } = promptResult;

  return (
    <Box>
      {queryResultCsvS3Bucket && queryResultCsvS3Path && (
        <Box>
          <Flex alignItems="center">
            {/* @dani add back in when preview csv streaming pr is in  */}
            {/* <Text fontWeight={600}>Results Preview</Text>
            <Text ml="16px" color={colors.black[70]}>
              Showing [GET NUMBER OF ROWS]
            </Text> */}
            <Spacer />
            <Button
              variant="ghost"
              onClick={() => getAthenaSqlGenerationSignedUrls()}
              isDisabled={isSignedUrlLoading}
            >
              Download Full Results
            </Button>
          </Flex>
          {/* @dani add back in when preview csv streaming pr is in  */}
          {/* <CsvResultPreviewTable
            finleyAiPromptResponseId={finleyAiPromptResponseId}
          /> */}
        </Box>
      )}

      {sql && (
        <>
          <Text>How can I help?</Text>
          <PromptSuggestionButton
            label="Explain the SQL query fully"
            onClick={() => handleAthenaSqlExplain(sql)}
          />
        </>
      )}
    </Box>
  );
};

const AthenaSqlPromptResponseOptions = ({
  promptResponse,
  handleAthenaSqlExplain,
}: {
  promptResponse: FinleyAi.AthenaSqlGenerationPromptResponse;
  handleAthenaSqlExplain: (sql: string) => void;
}) => {
  const { promptResult } = promptResponse;

  if (!promptResult) {
    return null;
  }

  switch (promptResult.subType) {
    case 'sqlGeneration':
      return (
        <SqlGeneratePromptResultOptions
          promptResult={promptResult}
          finleyAiPromptResponseId={promptResponse.id}
          handleAthenaSqlExplain={handleAthenaSqlExplain}
        />
      );
    case 'sqlExplain':
      return null;
    default:
      return finleyAssertUnreachable(promptResult);
  }
};

const PromptResponseResultOptions = ({
  promptResponse,
  handleAthenaSqlExplain,
}: {
  promptResponse: FinleyAi.FinleyAiPromptResponse;
  handleAthenaSqlExplain: (sql: string) => void;
}) => {
  switch (promptResponse.type) {
    case 'athenaSqlGeneration':
      return (
        <AthenaSqlPromptResponseOptions
          promptResponse={promptResponse}
          handleAthenaSqlExplain={handleAthenaSqlExplain}
        />
      );
    case 'singleCreditAgreementQuestion':
      return null;
    default:
      return finleyAssertUnreachable(promptResponse);
  }
};

type FinleyAiThreadHistoryProps = {
  thread: FinleyAi.FinleyAiThreadWithPromptResponses;
  isThreadDisabled: boolean;
  isProducingMessage: boolean;
  isGeneratingPromptResponse: boolean;
  handleAthenaSqlExplain: (sql: string) => void;
  onClickSqlAction: OnClickSqlAction;
  tempPrompt: string;
};
export const FinleyAiThreadHistory = ({
  thread,
  isThreadDisabled,
  isProducingMessage,
  isGeneratingPromptResponse,
  handleAthenaSqlExplain,
  onClickSqlAction,
  tempPrompt,
}: FinleyAiThreadHistoryProps) => {
  const threadUserId = thread.userId;
  const { user } = useUserAuthOrThrow();

  // for now, only allow users to access their own threads
  // In the future, we may want to display threads from scope outside of logged in user (eg. org)
  // and set up additional auth
  if (threadUserId !== user.id) {
    return (
      <Alert>
        <AlertIcon />
        You can only access your own threads.
      </Alert>
    );
  }

  return (
    <Flex flexDir="column" width="100%">
      {thread.promptResponses.map((pr, idx) => {
        const parsedPromptResultResponse = pr.promptResult?.response
          ? parsePromptResponse(pr.promptResult.response)
          : null;

        return (
          <Flex flexDir="column" key={`finley-ai-thread-history-${pr.id}`}>
            <PromptChatBubble promptString={pr.rawPrompt.prompt} />
            {parsedPromptResultResponse && !!parsedPromptResultResponse.length && (
              <Box mt="24px">
                <FinleyAiUserBadge label="Finley AI" type="finleyAi" />
                {parsedPromptResultResponse.map((promptResponseSegment) => {
                  if (promptResponseSegment.type === 'sql') {
                    return (
                      <FinleyAiPromptResponseSqlDisplay
                        sqlString={promptResponseSegment.value}
                        onClickSqlAction={onClickSqlAction}
                      />
                    );
                  }

                  return (
                    <Text
                      whiteSpace="pre-line"
                      mt="16px"
                      key={`${promptResponseSegment.value}`}
                      h="fit-content"
                    >
                      {promptResponseSegment.value}
                    </Text>
                  );
                })}
                <PromptResponseResultOptions
                  promptResponse={pr}
                  handleAthenaSqlExplain={handleAthenaSqlExplain}
                />
              </Box>
            )}

            {idx !== thread.promptResponses.length - 1 && (
              <Spacer minHeight="24px" />
            )}
          </Flex>
        );
      })}

      {/* after user enters a prompt, before the worklease is generated and returns the thread
          we want to simulate the user's prompt successfully sending by showing the tempPrompt immediately */}
      {isProducingMessage && (
        <Box mt="24px">
          <PromptChatBubble promptString={tempPrompt} />
        </Box>
      )}
      {(isProducingMessage || isGeneratingPromptResponse) && (
        <Box>
          <FinleyThinkingSpinner />
        </Box>
      )}

      {isThreadDisabled && (
        <Text>
          This thread is in a failed state. Please start a new thread.
        </Text>
      )}

      <br />
    </Flex>
  );
};

type FinleyAiThreadWindowProps = {
  thread: FinleyAi.FinleyAiThreadWithPromptResponses | null;
  isThreadDisabled: boolean;
  isProducingMessage: boolean;
  isGeneratingPromptResponse: boolean;
  handleAthenaSqlExplain: (sql: string) => void;
  onClickSqlAction: OnClickSqlAction;
  tempPrompt: string;
};
export const FinleyAiThreadWindow = ({
  thread,
  isThreadDisabled,
  isProducingMessage,
  isGeneratingPromptResponse,
  handleAthenaSqlExplain,
  onClickSqlAction,
  tempPrompt,
}: FinleyAiThreadWindowProps) => {
  if (!thread) {
    if (isProducingMessage || isGeneratingPromptResponse) {
      return <FinleyThinkingSpinner />;
    }

    return (
      <Text fontStyle="italic" textColor={colors.gray[600]}>
        Start a new thread...
      </Text>
    );
  }

  return (
    <FinleyAiThreadHistory
      thread={thread}
      isThreadDisabled={isThreadDisabled}
      isProducingMessage={isProducingMessage}
      isGeneratingPromptResponse={isGeneratingPromptResponse}
      handleAthenaSqlExplain={handleAthenaSqlExplain}
      onClickSqlAction={onClickSqlAction}
      tempPrompt={tempPrompt}
    />
  );
};
